import {ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, ViewChild} from '@angular/core';
import {LightTableColumn} from '../../common/light-table/light-table.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Modal} from 'bootstrap';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {NavigationService} from '../../../Services/navigation.service';
import {ChatService} from '../../../Services/chat.service';
import {AuthService} from '../../../Services/auth.service';
import {EmailModel} from '../../../DataModels/email-model';
import {SendEmailModalComponent} from '../../common/send-email-modal/send-email-modal.component';
import {AuditTrailModalComponent} from '../../common/audit-trail-modal/audit-trail-modal.component';
import {
  BillingAssemblePreview,
  Project,
  ProjectBillingEvent,
  ProjectKpis,
  ProjectMaterial,
  ProjectMilestone,
  ProjectNote,
  ProjectTask,
  ProjectUserRate,
  TmLine
} from '../../../DataModels/project.model';
import {TransactionEntityService} from '../../../Services/transaction-entity.service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';

@Component({
  selector: 'app-project-detail',
  templateUrl: './project-detail.component.html',
  styleUrls: ['./project-detail.component.css'],
  standalone: false
})
export class ProjectDetailComponent implements OnInit {
  vm: any = {
    title: 'Project Detail',
    item: null as Project,
    kpis: null as ProjectKpis,
    tasks: [] as ProjectTask[],
    milestones: [] as ProjectMilestone[],
    materials: [] as ProjectMaterial[],
    notes: [] as ProjectNote[],
    billingEvents: [] as ProjectBillingEvent[],
    timeEntries: [] as any[],
    users: [],
    projectUserRates: [] as ProjectUserRate[],
    Preferences: [],
    permissions: [],
    listRoute: '/projects/project-list',
    formRoute: '/projects/project-form',
    activeTab: 'overview',
    newNote: '',
    billingPreview: null as BillingAssemblePreview,
    showBillingPreview: false,
    buttonsDisabled: false,
  };

  isLoaded = false;
  projectId: string = null;

  // Task form
  showTaskForm = false;
  editingTask: ProjectTask = null;
  taskStatusOptions = ['Not Started', 'In Progress', 'Blocked', 'Done'];

  // Milestone form
  showMilestoneForm = false;
  editingMilestone: ProjectMilestone = null;

  // Material form
  showMaterialForm = false;
  editingMaterial: ProjectMaterial = null;
  inventoryItems: any[] = [];

  // Team Rates form
  showRateForm = false;
  editingRate: ProjectUserRate = null;

  // Invoice dialog state
  showInvoiceDialog = false;
  invoiceDialogEvent: ProjectBillingEvent | null = null;
  invoiceForm: { revenue_account_id: string; term_id: string; materials: any[]; labor_lines: any[] } = {
    revenue_account_id: '',
    term_id: '',
    materials: [],
    labor_lines: []
  };
  stagedMaterials: any[] = [];  // cached from assemble preview before billingPreview is cleared
  stagedTimeEntries: any[] = [];  // cached time entries from assemble preview
  invoiceAccounts: any[] = [];
  invoiceTerms: any[] = [];

  // Table column definitions
  materialsColumns: LightTableColumn[] = [
    {key: 'description', label: 'Description'},
    {key: 'date', label: 'Date', format: 'date'},
    {key: 'quantity', label: 'Qty', align: 'right', format: 'number'},
    {key: 'unit_price', label: 'Unit Price', align: 'right', format: 'currency'},
    {key: 'line_total', label: 'Total', align: 'right', format: 'currency'},
    {key: 'billable', label: 'Billable', format: 'boolean', align: 'center'},
    {key: 'billed', label: 'Billed', align: 'center', sortable: false},
    {key: 'actions', label: '', sortable: false, align: 'right'},
  ];

  timeColumns: LightTableColumn[] = [
    {key: 'entry_date', label: 'Date', format: 'date'},
    {key: 'user_id', label: 'Employee'},
    {key: 'task_id', label: 'Task', sortable: false},
    {key: 'hours', label: 'Hours', align: 'right', format: 'number'},
    {key: 'entry_type', label: 'Type'},
    {key: 'billable', label: 'Billable', align: 'center', sortable: false},
    {key: 'billed', label: 'Billed', align: 'center', sortable: false},
  ];

  materialRowClass = (row: any): string => '';
  timeRowClass = (row: any): string => '';

  // Task notify modal state
  notifyModal: { calendar: boolean; email: boolean; chat: boolean } = {calendar: true, email: true, chat: true};
  private notifyModalInstance: Modal | null = null;
  taskToNotify: ProjectTask | null = null;

  // Email modal
  reportDataToSendInEmail: any = {};
  autoSendEmailData: any = null;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private emailModalHost: ElementRef;
  @ViewChild(AuditTrailModalComponent) private auditTrailModal: AuditTrailModalComponent;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private nav: NavigationService,
    private chatService: ChatService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private entityService: TransactionEntityService,
    private confirmDialogService: ConfirmDialogService,
  ) {
  }

  ngOnInit() {
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.vm.permissions = JSON.parse(this.localStorage.StorageGet('Permissions'));

    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.vm.users = result.Data ?? result.items ?? [];
      },
      error: (err) => {
        console.error(err);
      },
    });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params['id']) {
        this.projectId = params['id'];
        this.loadProject();
      }
      if (params['tab']) {
        this.vm.activeTab = params['tab'];
      }
    });
  }

  loadProject() {
    this.tableDataService.getData('project', this.projectId).subscribe({
      next: (result: any) => {
        this.vm.item = result;
        this.titleService.setTitle(result.number + ' — ' + result.name);
        this.isLoaded = true;
        if (Array.isArray(result.tasks)) {
          this.vm.tasks = result.tasks;
        }
        this.loadKpis();
        this.loadMilestones();
        this.loadMaterials();
        this.loadNotes();
        this.loadBillingEvents();
        this.loadTimeEntries();
        this.loadProjectUserRates();
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading project');
      },
    });
  }

  loadKpis() {
    this.tableDataService.getData('project-dashboard', this.projectId).subscribe({
      next: (result: ProjectKpis) => {
        this.vm.kpis = result;
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadTasks() {
    this.tableDataService.getData('project', this.projectId).subscribe({
      next: (result: any) => {
        this.vm.tasks = Array.isArray(result?.tasks) ? result.tasks : [];
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadMilestones() {
    this.tableDataService.getDataWithParams('project-milestone', 'project_id=' + this.projectId).subscribe({
      next: (result: any) => {
        this.vm.milestones = Array.isArray(result) ? result : (result.items ?? result.Data ?? []);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadMaterials() {
    this.tableDataService.getDataWithParams('project-material', 'project_id=' + this.projectId).subscribe({
      next: (result: any) => {
        this.vm.materials = Array.isArray(result) ? result : (result.items ?? result.Data ?? []);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadNotes() {
    this.tableDataService.getDataWithParams('project-note', 'project_id=' + this.projectId).subscribe({
      next: (result: any) => {
        this.vm.notes = Array.isArray(result) ? result : (result.items ?? result.Data ?? []);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadBillingEvents() {
    this.tableDataService.getDataWithParams('project-billing', 'project_id=' + this.projectId).subscribe({
      next: (result: any) => {
        console.log('billingEvents', result);
        this.vm.billingEvents = Array.isArray(result) ? result : (result.items ?? result.Data ?? []);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadTimeEntries() {
    this.tableDataService.getDataWithParams('pr_time_entry', 'project_id=' + this.projectId).subscribe({
      next: (result: any) => {
        this.vm.timeEntries = Array.isArray(result) ? result : (result.items ?? result.Data ?? []);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  loadProjectUserRates() {
    this.tableDataService.getDataWithParams('project-user-rate', 'project_id=' + this.projectId).subscribe({
      next: (result: any) => {
        this.vm.projectUserRates = Array.isArray(result) ? result : (result.items ?? result.Data ?? []);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  setTab(tab: string) {
    this.vm.activeTab = tab;
  }

  // ── Team Rates ──

  openNewRate() {
    this.editingRate = {id: null, project_id: this.projectId, user_id: '', bill_rate: null, cost_rate: null} as any;
    this.showRateForm = true;
  }

  editRate(rate: ProjectUserRate) {
    this.editingRate = {...rate};
    this.showRateForm = true;
  }

  cancelRateForm() {
    this.showRateForm = false;
    this.editingRate = null;
  }

  saveRate() {
    if (!this.editingRate.user_id) {
      this.notification.showWarningToast('Please select a user.');
      return;
    }
    this.vm.buttonsDisabled = true;
    if (this.editingRate.id) {
      this.tableDataService.put('project-user-rate', this.editingRate.id, this.editingRate).subscribe({
        next: () => {
          this.notification.showSuccessToast('Rate updated.');
          this.cancelRateForm();
          this.loadProjectUserRates();
          this.vm.buttonsDisabled = false;
        },
        error: (err) => {
          console.error(err);
          this.notification.showErrorToast('Error saving rate.');
          this.vm.buttonsDisabled = false;
        },
      });
    } else {
      this.tableDataService.post('project-user-rate', this.editingRate).subscribe({
        next: () => {
          this.notification.showSuccessToast('Rate override added.');
          this.cancelRateForm();
          this.loadProjectUserRates();
          this.vm.buttonsDisabled = false;
        },
        error: (err) => {
          console.error(err);
          this.notification.showErrorToast('Error saving rate.');
          this.vm.buttonsDisabled = false;
        },
      });
    }
  }

  deleteRate(id: string) {
    if (!confirm('Remove this rate override?')) {
      return;
    }
    this.vm.buttonsDisabled = true;
    this.tableDataService.deleteDataById('project-user-rate', id).subscribe({
      next: () => {
        this.notification.showSuccessToast('Rate override removed.');
        this.loadProjectUserRates();
        this.vm.buttonsDisabled = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error removing rate.');
        this.vm.buttonsDisabled = false;
      },
    });
  }

  onEditProject() {
    this.router.navigate([this.vm.formRoute], {queryParams: {id: this.projectId}}).then();
  }

  onClose() {
    this.nav.backOr(this.vm.listRoute);
  }

  // ── Tasks ──────────────────────────────────────────────────────────────────

  openNewTask() {
    this.editingTask = {
      project_id: this.projectId,
      name: '',
      status: 'Not Started',
      estimated_hours: 0,
      billable: true,
      percent_complete: 0,
      sort_order: this.vm.tasks.length + 1
    };
    this.showTaskForm = true;
  }

  openEditTask(task: ProjectTask) {
    this.editingTask = {...task};
    this.showTaskForm = true;
  }

  saveTask() {
    if (!this.editingTask?.name) {
      return;
    }
    this.vm.buttonsDisabled = true;
    if (!this.editingTask.id) {
      this.tableDataService.post('project-task', this.editingTask).subscribe({
        next: (result: any) => {
          this.editingTask.id = result.id;
          this.notification.showSuccessToast('Task saved');
          this.showTaskForm = false;
          this.loadTasks();
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    } else {
      const payload = {...this.editingTask};
      if (this.vm.item?.billing_type === 'TimeAndMaterials') {
        delete payload.percent_complete;
      }
      this.tableDataService.put('project-task', this.editingTask.id, payload).subscribe({
        next: () => {
          this.notification.showSuccessToast('Task updated');
          this.showTaskForm = false;
          this.loadTasks();
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    }
  }

  deleteTask(task: ProjectTask) {
    if (!confirm('Delete task "' + task.name + '"?')) {
      return;
    }
    this.tableDataService.deleteDataById('project-task', task.id).subscribe({
      next: () => {
        this.notification.showSuccessToast('Task deleted');
        this.loadTasks();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  cancelTaskForm() {
    this.showTaskForm = false;
    this.editingTask = null;
  }

  openAuditTrailModal() {
    this.auditTrailModal?.toggleAudit();
  }

  openNotifyTaskModal(task: ProjectTask): void {
    if (!task.assigned_user_id) {
      this.notification.showWarningToast('Please assign a user to this task before notifying.');
      return;
    }
    if (!task.id) {
      this.notification.showWarningToast('Please save the task before notifying.');
      return;
    }
    this.taskToNotify = task;
    const el = document.getElementById('notifyTaskModal');
    if (el) {
      this.notifyModalInstance = new Modal(el);
      this.notifyModalInstance.show();
    }
  }

  async sendTaskNotifications(): Promise<void> {
    this.notifyModalInstance?.hide();
    const task = this.taskToNotify;
    if (!task) {
      return;
    }
    const assignee = this.vm.users.find((u: any) => u.id === task.assigned_user_id);
    const projectNumber = this.vm.item?.number ?? '';
    const projectName = this.vm.item?.name ?? '';
    const taskLink = `${window.location.origin}/projects/project-detail?id=${this.projectId}&tab=tasks`;
    const results: string[] = [];
    const errors: string[] = [];

    if (this.notifyModal.calendar && task.due_date) {
      const calendarEvent: any = {
        Id: '',
        Title: `Task: ${task.name} — ${projectNumber}`,
        ClassNames: 'bg-info-subtle',
        AllDay: true,
        Start: task.due_date,
        Attendees: [{UserId: task.assigned_user_id, Status: 'Pending'}],
        ExtendedProps: {
          Description: `Project: ${projectName}\nTask: ${task.name}\n${task.description ?? ''}\n\n${taskLink}`,
          IsOrganizer: true,
          MyStatus: 'Organizer',
          Attendees: [{UserId: task.assigned_user_id, Status: 'Pending'}],
          Category: 'Task',
          Priority: 'info'
        }
      };
      await new Promise<void>(resolve => {
        this.tableDataService.post('Calendar', calendarEvent).subscribe({
          next: () => {
            results.push('Calendar event created');
            resolve();
          },
          error: () => {
            errors.push('Calendar failed');
            resolve();
          }
        });
      });
    } else if (this.notifyModal.calendar && !task.due_date) {
      errors.push('Calendar: task has no due date');
    }

    if (this.notifyModal.email) {
      if (assignee?.email) {
        const emailDocObj: any = {} as EmailModel;
        emailDocObj.toEmail = assignee.email;
        emailDocObj.Subject = `Task Assignment — ${task.name} (${projectNumber})`;
        emailDocObj.body = `Hello ${assignee.name},<br><br>You have been assigned a task on project <b>${projectNumber} — ${projectName}</b>.<br><br><b>Task:</b> ${task.name}<br><b>Status:</b> ${task.status}<br><b>Due Date:</b> ${task.due_date ?? 'Not set'}<br><b>Estimated Hours:</b> ${task.estimated_hours ?? 0}<br><br><a href="${taskLink}">Click here to view the task</a><br><br>Thank you.`;
        emailDocObj.fromEmail = '';
        emailDocObj.fromName = 'AcctXErp';
        emailDocObj.convert_attachment_to = 'pdf';
        emailDocObj.includeAttachment = false;
        this.reportDataToSendInEmail = emailDocObj;
        this.autoSendEmailData = {...emailDocObj};
        this.cdr.detectChanges();
        results.push('Email sent to ' + assignee.email);
      } else {
        errors.push('Email: assignee has no email address');
      }
    }

    if (this.notifyModal.chat) {
      const currentUser = this.authService.currentUser();
      const senderId = (currentUser?.id ?? currentUser?.Id ?? '').toString();
      if (senderId && task.assigned_user_id) {
        const chatBody = `📋 Task Assignment: *${task.name}*\nProject: ${projectNumber} — ${projectName}\nStatus: ${task.status}\nDue: ${task.due_date ?? 'Not set'}\n🔗 ${taskLink}`;
        await new Promise<void>(resolve => {
          this.chatService.createOrGetThread(senderId, task.assigned_user_id).subscribe({
            next: (thread: any) => {
              this.chatService.sendMessage(thread.id, senderId, chatBody).subscribe({
                next: () => {
                  results.push('Chat message sent');
                  resolve();
                },
                error: () => {
                  errors.push('Chat failed');
                  resolve();
                }
              });
            },
            error: () => {
              errors.push('Chat: could not create thread');
              resolve();
            }
          });
        });
      } else {
        errors.push('Chat: sender not identified');
      }
    }

    if (results.length) {
      this.notification.showSuccessToast('✅ ' + results.join(' · '));
    }
    if (errors.length) {
      this.notification.showErrorToast('⚠️ ' + errors.join(' · '));
    }
    this.taskToNotify = null;
  }

  taskBadgeClass(status: string): string {
    switch (status) {
      case 'In Progress':
        return 'bg-primary';
      case 'Blocked':
        return 'bg-danger';
      case 'Done':
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  }

  // ── Milestones ─────────────────────────────────────────────────────────────

  openNewMilestone() {
    this.editingMilestone = {project_id: this.projectId, name: '', invoice_on_completion: false};
    this.showMilestoneForm = true;
  }

  openEditMilestone(m: ProjectMilestone) {
    this.editingMilestone = {...m};
    this.showMilestoneForm = true;
  }

  saveMilestone() {
    if (!this.editingMilestone?.name) {
      return;
    }
    this.vm.buttonsDisabled = true;
    if (!this.editingMilestone.id) {
      this.tableDataService.post('project-milestone', this.editingMilestone).subscribe({
        next: () => {
          this.notification.showSuccessToast('Milestone saved');
          this.showMilestoneForm = false;
          this.loadMilestones();
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    } else {
      this.tableDataService.put('project-milestone', this.editingMilestone.id, this.editingMilestone).subscribe({
        next: () => {
          this.notification.showSuccessToast('Milestone updated');
          this.showMilestoneForm = false;
          this.loadMilestones();
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    }
  }

  completeMilestone(m: ProjectMilestone) {
    if (!confirm('Mark milestone "' + m.name + '" as complete?')) {
      return;
    }
    this.tableDataService.post('project-milestone/' + m.id + '/complete', {}).subscribe({
      next: () => {
        this.notification.showSuccessToast('Milestone completed');
        this.loadMilestones();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  deleteMilestone(m: ProjectMilestone) {
    if (!confirm('Delete milestone "' + m.name + '"?')) {
      return;
    }
    this.tableDataService.deleteDataById('project-milestone', m.id).subscribe({
      next: () => {
        this.notification.showSuccessToast('Milestone deleted');
        this.loadMilestones();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  cancelMilestoneForm() {
    this.showMilestoneForm = false;
    this.editingMilestone = null;
  }

  // ── Materials ──────────────────────────────────────────────────────────────

  openNewMaterial() {
    const currentUser = this.authService.currentUser();
    const userId = (currentUser?.id ?? currentUser?.Id ?? '').toString();
    this.editingMaterial = {
      project_id: this.projectId,
      item_id: null,
      description: '',
      date: new Date().toISOString().substring(0, 10),
      quantity: 1,
      unit_cost: 0,
      unit_price: 0,
      billable: true,
      added_by_user_id: userId,
      sort_order: this.vm.materials.length + 1
    };
    this.loadInventoryItems();
    this.showMaterialForm = true;
  }

  openEditMaterial(mat: ProjectMaterial) {
    if (mat.billed) {
      return;
    }
    this.editingMaterial = {...mat};
    this.loadInventoryItems();
    this.showMaterialForm = true;
  }

  loadInventoryItems() {
    if (this.inventoryItems.length > 0) {
      return;
    }
    this.tableDataService.getData('item', '?pageNumber=0&pageSize=999999').subscribe({
      next: (res: any) => {
        this.inventoryItems = res?.Data ?? res?.data ?? (Array.isArray(res) ? res : []);
      },
      error: () => {
      }
    });
  }

  onMaterialItemSelected() {
    if (!this.editingMaterial?.item_id) {
      return;
    }
    const found = this.inventoryItems.find((i: any) => i.id === this.editingMaterial.item_id);
    if (found) {
      this.editingMaterial.description = found.name ?? found.description ?? this.editingMaterial.description;
      this.editingMaterial.unit_price = found.price ?? found.unit_price ?? this.editingMaterial.unit_price;
      this.editingMaterial.unit_cost = found.cost ?? found.unit_cost ?? this.editingMaterial.unit_cost;
    }
  }

  saveMaterial() {
    if (!this.editingMaterial?.description) {
      return;
    }
    this.vm.buttonsDisabled = true;
    if (!this.editingMaterial.id) {
      this.tableDataService.post('project-material', this.editingMaterial).subscribe({
        next: () => {
          this.notification.showSuccessToast('Material saved');
          this.showMaterialForm = false;
          this.loadMaterials();
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    } else {
      this.tableDataService.put('project-material', this.editingMaterial.id, this.editingMaterial).subscribe({
        next: () => {
          this.notification.showSuccessToast('Material updated');
          this.showMaterialForm = false;
          this.loadMaterials();
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
        },
      });
    }
  }

  deleteMaterial(mat: ProjectMaterial) {
    if (mat.billed) {
      return;
    }
    if (!confirm('Delete this material line?')) {
      return;
    }
    this.tableDataService.deleteDataById('project-material', mat.id).subscribe({
      next: () => {
        this.notification.showSuccessToast('Material deleted');
        this.loadMaterials();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  cancelMaterialForm() {
    this.showMaterialForm = false;
    this.editingMaterial = null;
  }

  // ── Notes ──────────────────────────────────────────────────────────────────

  addNote() {
    if (!this.vm.newNote?.trim()) {
      return;
    }
    const currentUser = this.authService.currentUser();
    const userId = (currentUser?.id ?? currentUser?.Id ?? '').toString();
    const payload = {project_id: this.projectId, user_id: userId, note: this.vm.newNote.trim()};
    this.tableDataService.post('project-note', payload).subscribe({
      next: () => {
        this.vm.newNote = '';
        this.loadNotes();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  deleteNote(note: ProjectNote) {
    if (!confirm('Delete this note?')) {
      return;
    }
    this.tableDataService.deleteDataById('project-note', note.id).subscribe({
      next: () => {
        this.loadNotes();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  // ── Billing ────────────────────────────────────────────────────────────────

  billingTaskLines: { user_id: string; task_id: string | null; task_name: string; hours: number; description: string }[] = [];

  assembleBilling() {
    this.vm.buttonsDisabled = true;
    this.tableDataService.post('project-billing/assemble/' + this.projectId, {}).subscribe({
      next: (result: BillingAssemblePreview) => {
        console.log('billing', result);
        this.vm.billingPreview = result;
        this.billingTaskLines = this.getTaskLines(result.time_entries ?? []);
        this.vm.showBillingPreview = true;
        this.vm.buttonsDisabled = false;
      },
      error: (err) => {
        console.error(err);
        this.vm.buttonsDisabled = false;
      },
    });
  }

  confirmBilling() {
    if (!this.vm.billingPreview) {
      return;
    }
    this.vm.buttonsDisabled = true;
    const payload: ProjectBillingEvent = {
      project_id: this.projectId,
      billing_date: new Date().toISOString().substring(0, 10),
      percent_billed: this.vm.billingPreview.percent_billed,
      hours_billed: this.vm.billingPreview.total_hours,
      materials_amount: this.vm.billingPreview.materials_total,
      amount_billed: this.vm.billingPreview.amount_billed,
    };
    this.tableDataService.post('project-billing/confirm', payload).subscribe({
      next: () => {
        this.notification.showSuccessToast('Billing event created');
        // Cache materials (with item_id) and time entries before clearing the preview
        this.stagedMaterials = this.vm.billingPreview?.materials ?? [];
        this.stagedTimeEntries = this.vm.billingPreview?.time_entries ?? [];
        this.billingTaskLines = [];
        this.vm.showBillingPreview = false;
        this.vm.billingPreview = null;
        this.loadBillingEvents();
      },
      error: (err) => {
        console.error(err);
      },
      complete: () => {
        this.vm.buttonsDisabled = false;
      },
    });
  }

  cancelBillingPreview() {
    this.vm.showBillingPreview = false;
    this.vm.billingPreview = null;
  }

  invoiceBillingEvent(event: ProjectBillingEvent) {
    this.invoiceDialogEvent = event;
    this.invoiceForm = {revenue_account_id: '', term_id: '', materials: [], labor_lines: []};
    this.showInvoiceDialog = true;
    // Populate labor_lines from cached time entries or re-fetch
    // Enrich each labor line with item_id and account_id from the project's default_labor_item_id
    const assignLaborLines = (entries: any[]) => {
      const laborItemId = this.vm.item?.default_labor_item_id ?? null;
      const laborItem = laborItemId ? this.inventoryItems.find((i: any) => i.id === laborItemId) : null;
      this.invoiceForm.labor_lines = this.getTaskLines(entries).map((l: any) => ({
        ...l,
        item_id: laborItemId ?? null,
        ...(laborItem?.revenue_account_id ? {account_id: laborItem.revenue_account_id} : {}),
      }));
    };
    const runAssignLaborLines = (entries: any[]) => {
      if (this.inventoryItems.length > 0) {
        assignLaborLines(entries);
      } else {
        this.tableDataService.getData('item', '?pageNumber=0&pageSize=999999').subscribe({
          next: (res: any) => {
            this.inventoryItems = res?.Data ?? res?.data ?? (Array.isArray(res) ? res : []);
            assignLaborLines(entries);
          },
          error: () => assignLaborLines(entries),
        });
      }
    };
    if (this.stagedTimeEntries.length) {
      runAssignLaborLines(this.stagedTimeEntries);
    } else {
      this.tableDataService.getData('project-billing/assemble', this.projectId).subscribe({
        next: (preview: any) => {
          if (preview?.time_entries?.length) {
            this.stagedTimeEntries = preview.time_entries;
            runAssignLaborLines(preview.time_entries);
          }
        },
        error: (err) => console.error('[InvoiceDialog] assemble labor error:', err),
      });
    }
    // Use cached staged materials (item_id preserved); fall back to re-fetch only if cache is empty
    // Resolve account_id from inventory items after items are loaded to avoid race condition
    const assignMaterials = (mats: any[]) => {
      this.invoiceForm.materials = mats.map((m: any) => {
        const line = {...m};
        if (m.item_id) {
          const invItem = this.inventoryItems.find((i: any) => i.id === m.item_id);
          if (invItem?.revenue_account_id) {
            line.account_id = invItem.revenue_account_id;
          }
        }
        return line;
      });
    };
    if (this.stagedMaterials.length) {
      if (this.inventoryItems.length > 0) {
        assignMaterials(this.stagedMaterials);
      } else {
        this.tableDataService.getData('item', '?pageNumber=0&pageSize=999999').subscribe({
          next: (res: any) => {
            this.inventoryItems = res?.Data ?? res?.data ?? (Array.isArray(res) ? res : []);
            assignMaterials(this.stagedMaterials);
          },
          error: () => {
            this.invoiceForm.materials = this.stagedMaterials;
          }
        });
      }
    } else {
      this.tableDataService.getData('project-billing/assemble', this.projectId).subscribe({
        next: (preview: any) => {
          if (preview?.materials?.length) {
            if (this.inventoryItems.length > 0) {
              assignMaterials(preview.materials);
            } else {
              this.tableDataService.getData('item', '?pageNumber=0&pageSize=999999').subscribe({
                next: (res: any) => {
                  this.inventoryItems = res?.Data ?? res?.data ?? (Array.isArray(res) ? res : []);
                  assignMaterials(preview.materials);
                },
                error: () => {
                  this.invoiceForm.materials = preview.materials;
                }
              });
            }
          }
        },
        error: (err) => console.error('[InvoiceDialog] assemble preview error:', err),
      });
    }
    // Pre-populate default term from entity
    if (this.vm.item?.entity_id) {
      this.entityService.loadEntityContext(this.vm.item.entity_id).subscribe({
        next: (ctx: any) => {
          console.log('[InvoiceDialog] entity context from loadEntityContext:', ctx);
          const defaultTermId = ctx?.entity?.term_id ?? null;
          if (defaultTermId && !this.invoiceForm.term_id) {
            this.invoiceForm.term_id = defaultTermId;
          }
          const defaultRevenueAccountId = ctx?.entity?.project_revenue_account_id ?? null;
          console.log('[InvoiceDialog] project_revenue_account_id from ctx.entity:', defaultRevenueAccountId);
          if (defaultRevenueAccountId && !this.invoiceForm.revenue_account_id) {
            this.invoiceForm.revenue_account_id = defaultRevenueAccountId;
          }
        },
        error: (err) => console.error(err),
      });
    }
  }

  cancelInvoiceDialog() {
    this.showInvoiceDialog = false;
    this.invoiceDialogEvent = null;
  }

  submitInvoiceDialog() {
    const event = this.invoiceDialogEvent;
    this.showInvoiceDialog = false;
    this.invoiceDialogEvent = null;
    this.vm.buttonsDisabled = true;
    const body: any = {revenue_account_id: this.invoiceForm.revenue_account_id};
    if (this.invoiceForm.term_id) {
      body.term_id = this.invoiceForm.term_id;
    }
    console.log('this.invoiceForm', this.invoiceForm);
    if (this.invoiceForm.labor_lines?.length) {
      body.labor_lines = this.invoiceForm.labor_lines.map((l: any) => ({
        user_id: l.user_id,
        task_id: l.task_id ?? null,
        ...(l.item_id ? {item_id: l.item_id} : {}),
      }));
    }
    if (this.invoiceForm.materials?.length) {
      body.materials = this.invoiceForm.materials.map((m: any) => ({
        id: m.id,
        item_id: m.item_id ?? null,
      }));
    }
    console.log('invoiceForm', body);

    this.tableDataService.post('project-billing/invoice/' + event.id, body).subscribe({
      next: (result: any) => {
        this.notification.showSuccessToast('Invoice created');
        this.stagedMaterials = [];  // clear cache after successful invoice
        this.stagedTimeEntries = [];
        this.loadBillingEvents();
        if (result?.invoice_id) {
          this.router.navigate(['/transactions/invoice/ar/form'], {queryParams: {invoiceId: result.invoice_id}}).then();
        }
      },
      error: (err) => {
        console.error(err);
        const msg = err?.error?.message ?? err?.error ?? 'Error creating invoice';
        this.notification.showErrorToast(msg);
        this.vm.buttonsDisabled = false;
      },
      complete: () => {
        this.vm.buttonsDisabled = false;
      },
    });
  }

  deleteBillingEvent(event: ProjectBillingEvent) {
    const msg = event.invoiced
      ? 'This billing event has an invoice. Deleting will void the invoice and reset all time entries and materials to unbilled. Continue?'
      : 'Delete this billing event? All time entries and materials will be reset to unbilled.';
    this.confirmDialogService.confirmThis([msg], () => {
      this.tableDataService.deleteDataById('project-billing', event.id).subscribe({
        next: (result: any) => {
          let toast: string;
          if (result?.invoice_not_found) {
            toast = 'Billing deleted. Note: the invoice had already been removed. Project data has been reset for re-invoicing.';
          } else if (result?.invoice_voided) {
            toast = `Billing deleted. Invoice voided. ${result.time_entries_reset ?? 0} time entries and ${result.materials_reset ?? 0} materials have been reset to unbilled.`;
          } else {
            toast = `Billing deleted. ${result?.time_entries_reset ?? 0} time entries and ${result?.materials_reset ?? 0} materials have been reset to unbilled.`;
          }
          this.notification.showSuccessToast(toast);
          this.loadBillingEvents();
          this.loadTimeEntries();
          this.loadMaterials();
        },
        error: (err) => {
          console.error(err);
          const msg2 = err?.error?.error ?? err?.error?.message ?? err?.error ?? 'Error deleting billing event';
          this.notification.showErrorToast(msg2);
        },
      });
    }, () => {
    });
  }

  getTaskLines(timeEntries: any[]): { user_id: string; task_id: string | null; task_name: string; hours: number; description: string }[] {
    if (timeEntries.length) {
      console.log('[TaskLines] sample time entry:', timeEntries[0]);
    }
    const map = new Map<string, { user_id: string; task_id: string | null; task_name: string; hours: number }>();
    for (const te of timeEntries) {
      const key = `${te.user_id}|${te.task_id ?? ''}`;
      const existing = map.get(key);
      if (existing) {
        existing.hours += te.hours ?? 0;
      } else {
        map.set(key, {
          user_id: te.user_id,
          task_id: te.task_id ?? null,
          task_name: te.task_name ?? 'General / No Task',
          hours: te.hours ?? 0
        });
      }
    }
    return Array.from(map.values()).map(l => ({
      ...l,
      description: `${l.task_name} — ${l.hours.toFixed(2)} hrs`,
    }));
  }

  getUserName(userId: string): string {
    const user = this.vm.users.find((u: any) => u.id === userId);
    return user ? user.name : userId;
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'Active':
        return 'bg-success';
      case 'On Hold':
        return 'bg-warning text-dark';
      case 'Completed':
        return 'bg-primary';
      case 'Cancelled':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }
}
